import React, { useState, useEffect, useRef } from 'react';
import {
    View, Text, StyleSheet, ScrollView, TouchableOpacity,
    ActivityIndicator, FlatList, Modal, TextInput, Alert,
    Animated, Dimensions, Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    fetchSavingsGoals, createSavingsGoal, depositToSavings, withdrawFromSavings,
    fetchSavingsChallenges, joinSavingsChallenge, depositToChallenge
} from '../../api/pesa';

const { width: SW } = Dimensions.get('window');

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
    bg: '#FFFFFF',
    surface: '#F8FAFC',
    surfaceRaised: '#F1F5F9',
    border: '#E2E8F0',
    borderBright: '#CBD5E1',
    green: '#10B981',
    greenDark: '#059669',
    greenGlow: 'rgba(16,185,129,0.08)',
    teal: '#0EA5E9',
    gold: '#F59E0B',
    goldGlow: 'rgba(245,158,11,0.08)',
    purple: '#8B5CF6',
    purpleGlow: 'rgba(139,92,246,0.08)',
    red: '#EF4444',
    white: '#FFFFFF',
    gray50: '#F8FAFC',
    gray100: '#F1F5F9',
    gray300: '#CBD5E1',
    gray400: '#94A3B8',
    gray500: '#64748B',
    gray600: '#475569',
    text: '#0F172A',
    textMuted: '#64748B',
    textFaint: '#94A3B8',
    cardShadow: {
        shadowColor: '#64748B',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 3,
    }
};


// ── Cadence Badge ─────────────────────────────────────────────────────────────
const CadenceBadge = ({ cadence }) => {
    const cfg = {
        daily:   { color: C.teal,   bg: 'rgba(6,182,212,0.12)',   icon: 'today-outline' },
        weekly:  { color: C.purple, bg: 'rgba(139,92,246,0.12)',  icon: 'calendar-outline' },
        monthly: { color: C.gold,   bg: 'rgba(245,158,11,0.12)',  icon: 'calendar-clear-outline' },
    };
    const c = cfg[cadence] || cfg.monthly;
    return (
        <View style={[styles.badge, { backgroundColor: c.bg, borderColor: c.color + '40' }]}>
            <Ionicons name={c.icon} size={10} color={c.color} />
            <Text style={[styles.badgeTxt, { color: c.color }]}>{cadence?.toUpperCase()}</Text>
        </View>
    );
};

// ── Status Chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
    const cfg = {
        active:     { label: 'Active',     color: C.green },
        upcoming:   { label: 'Upcoming',   color: C.teal },
        completed:  { label: 'Completed',  color: C.gold },
        cancelled:  { label: 'Cancelled',  color: C.red },
        joined:     { label: 'Joined ✓',   color: C.green },
        failed:     { label: 'Failed',     color: C.red },
    };
    const c = cfg[status] || { label: status, color: C.gray400 };
    return (
        <View style={[styles.statusChip, { borderColor: c.color + '50', backgroundColor: c.color + '18' }]}>
            <Text style={[styles.statusTxt, { color: c.color }]}>{c.label}</Text>
        </View>
    );
};

// ── Main Screen ───────────────────────────────────────────────────────────────
const SavingsScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const [goals, setGoals] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('goals');    // 'goals' | 'challenges'
    const [showAmounts, setShowAmounts] = useState(false);


    // Modals
    const [createGoalModal, setCreateGoalModal] = useState(false);
    const [depositModal, setDepositModal] = useState(false);
    const [actionModal, setActionModal] = useState(false);
    const [challengeDepositModal, setChallengeDepositModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [featuredChallenge, setFeaturedChallenge] = useState(null);

    // Payment/Polling state
    const [isProcessing, setIsProcessing] = useState(false);
    const [pollingRef, setPollingRef] = useState(null);
    const lastBalanceSum = useRef(0);



    // Form state
    const [newGoalName, setNewGoalName] = useState('');
    const [newGoalTarget, setNewGoalTarget] = useState('');
    const [isFixedGoal, setIsFixedGoal] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [challengeDepositAmt, setChallengeDepositAmt] = useState('');
    const [challengePhone, setChallengePhone] = useState('');

    // Animated tab indicator
    const tabW = (SW - 32) / 2;
    const tabAnim = useRef(new Animated.Value(0)).current;
    const switchTab = (tab) => {
        Animated.spring(tabAnim, { toValue: tab === 'goals' ? 0 : tabW, useNativeDriver: true, damping: 18, stiffness: 200 }).start();
        setActiveTab(tab);
    };

    // Balance pulse animation
    const pulseAnim = useRef(new Animated.Value(1)).current;
    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, { toValue: 1.015, duration: 2000, useNativeDriver: true }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 2000, useNativeDriver: true }),
            ])
        ).start();
    }, []);

    const loadData = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const [g, ch] = await Promise.allSettled([fetchSavingsGoals(), fetchSavingsChallenges()]);
            if (g.status === 'fulfilled') setGoals(g.value);
            if (ch.status === 'fulfilled') {
                const now = new Date();
                const activeChallenges = ch.value.filter(c => new Date(c.end_date) >= now);
                setChallenges(activeChallenges);
                // Find most popular challenge among active ones
                const popular = [...activeChallenges].sort((a,b) => (b.participant_count || 0) - (a.participant_count || 0))[0];
                setFeaturedChallenge(popular);
            }
        } catch (e) {
            if (!silent) Alert.alert('Error', 'Failed to load savings data');
        } finally {
            if (!silent) setLoading(false);
        }
    };
    
    // Auto-stop polling if balance changes
    useEffect(() => {
        if (isProcessing) {
            const currentTotal = totalSaved + joinedChallenges.reduce((s, c) => s + parseFloat(c.my_participation?.saved_amount || 0), 0);
            if (currentTotal > lastBalanceSum.current) {
                setIsProcessing(false);
                if (pollingRef) {
                    clearInterval(pollingRef);
                    setPollingRef(null);
                }
            }
        }
    }, [goals, challenges]);



    useEffect(() => { 
        loadData(); 
        return () => { if (pollingRef) clearInterval(pollingRef); };
    }, []);

    const goalSavings = goals.reduce((s, g) => s + parseFloat(g.current_amount || 0), 0);
    const challengeSavings = challenges.filter(c => c.my_participation).reduce((s, c) => s + parseFloat(c.my_participation.saved_amount || 0), 0);
    const totalSaved = goalSavings + challengeSavings;
    
    const activeGoals = goals.filter(g => g.status !== 'completed');
    const completedGoals = goals.filter(g => g.status === 'completed');
    const completionRate = goals.length > 0 ? Math.round((completedGoals.length / goals.length) * 100) : 0;
    const joinedChallenges = challenges.filter(c => c.my_participation);


    // ── Create Goal ──────────────────────────────────────────────────────────
    const handleCreateGoal = async () => {
        if (!newGoalName || !newGoalTarget) return Alert.alert('Error', 'Fill in all fields');
        try {
            await createSavingsGoal({ name: newGoalName, target_amount: parseFloat(newGoalTarget), lock_funds: isFixedGoal });
            setCreateGoalModal(false); setNewGoalName(''); setNewGoalTarget(''); setIsFixedGoal(false);
            loadData(); Alert.alert('✓ Goal Created!', 'You earned 20 XP for creating a savings goal!');
        } catch { Alert.alert('Error', 'Failed to create goal'); }
    };

    const startPolling = () => {
        lastBalanceSum.current = totalSaved + joinedChallenges.reduce((s, c) => s + parseFloat(c.my_participation?.saved_amount || 0), 0);
        setIsProcessing(true);
        
        // Proactive: Initial silent poll immediately
        loadData(true);

        let count = 0;
        const interval = setInterval(async () => {
            count++;
            await loadData(true);
            if (count >= 12) { // Polling up to 60s at 5s intervals
                clearInterval(interval);
                setIsProcessing(false);
                setPollingRef(null);
            }
        }, 5000); // Back to 5s interval as requested
        setPollingRef(interval);
    };



    const handleDeposit = async () => {
        if (!depositAmount || isNaN(depositAmount)) return Alert.alert('Error', 'Enter valid amount');
        if (!phoneNumber) return Alert.alert('Error', 'Enter M-Pesa phone number');
        try {
            await depositToSavings(selectedGoal.id, parseFloat(depositAmount), phoneNumber);
            setDepositModal(false); setDepositAmount(''); setPhoneNumber('');
            startPolling();
        } catch { Alert.alert('Error', 'Deposit failed'); }
    };


    const handleWithdraw = async (goal) => {
        Alert.alert('Wind Down & Withdraw', `Withdraw KSH ${goal.current_amount} from "${goal.name}"?`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Withdraw', style: 'destructive', onPress: async () => {
                try {
                    await withdrawFromSavings(goal.id, parseFloat(goal.current_amount));
                    setActionModal(false); loadData(); Alert.alert('Success', 'Funds moved to wallet.');
                } catch (e) { Alert.alert('Error', e.response?.data?.error || 'Withdrawal failed'); }
            }}
        ]);
    };

    const handleJoinChallenge = async (challengeId) => {
        try {
            await joinSavingsChallenge(challengeId);
            Alert.alert('Joined! 🎯', "You're in the challenge. Start saving to hit the target!");
            loadData();
        } catch (e) {
            Alert.alert('Error', e.response?.data?.error || 'Could not join challenge');
        }
    };

    const handleChallengeDeposit = async () => {
        if (!challengeDepositAmt || isNaN(challengeDepositAmt)) return Alert.alert('Error', 'Enter valid amount');
        if (!challengePhone) return Alert.alert('Error', 'Enter M-Pesa phone number');
        try {
            await depositToChallenge(selectedChallenge.id, parseFloat(challengeDepositAmt), challengePhone);
            setChallengeDepositModal(false); setChallengeDepositAmt(''); setChallengePhone('');
            startPolling();
        } catch { Alert.alert('Error', 'Deposit failed'); }
    };


    // ── Goal Card ─────────────────────────────────────────────────────────────
    const GoalCard = ({ item }) => {
        const progress = Math.min((parseFloat(item.current_amount) / parseFloat(item.target_amount)) * 100, 100);
        const remaining = Math.max(parseFloat(item.target_amount) - parseFloat(item.current_amount), 0);
        const isCompleted = item.status === 'completed';

        return (
            <View style={styles.goalCard}>
                {item.lock_funds && <View style={styles.goalAccentTop} />}
                <View style={styles.goalCardHead}>
                    <View style={[styles.goalIconWrap, { backgroundColor: isCompleted ? 'rgba(16,185,129,0.15)' : item.lock_funds ? 'rgba(139,92,246,0.12)' : 'rgba(16,185,129,0.1)' }]}>
                        <Ionicons name={isCompleted ? 'checkmark-circle' : item.lock_funds ? 'lock-closed' : 'wallet'} size={20} color={isCompleted ? C.green : item.lock_funds ? C.purple : C.green} />
                    </View>
                    <View style={{ flex: 1, marginLeft: 10 }}>
                        <Text style={styles.goalName}>{item.name}</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <View style={[styles.miniChip, { backgroundColor: item.lock_funds ? C.purpleGlow : C.greenGlow }]}>
                                <Text style={[styles.miniChipTxt, { color: item.lock_funds ? C.purple : C.green }]}>{item.lock_funds ? 'Fixed' : 'Flexible'}</Text>
                            </View>
                            {isCompleted && <StatusChip status="completed" />}
                        </View>
                    </View>
                    <TouchableOpacity onPress={() => { setSelectedGoal(item); setActionModal(true); }} style={styles.moreBtn}>
                        <Ionicons name="ellipsis-vertical" size={18} color={C.gray500} />
                    </TouchableOpacity>
                </View>

                <View style={styles.goalAmounts}>
                    <View>
                        <Text style={styles.goalAmtLabel}>Saved</Text>
                        <Text style={styles.goalAmtValue}>{showAmounts ? `KSH ${parseFloat(item.current_amount).toLocaleString()}` : '• • • •'}</Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.goalAmtLabel}>Target</Text>
                        <Text style={[styles.goalAmtValue, { color: C.textMuted }]}>{showAmounts ? `KSH ${parseFloat(item.target_amount).toLocaleString()}` : '• • • •'}</Text>
                    </View>
                </View>

                <View style={styles.progressBarWrap}>
                    <View style={styles.progressBarBg}>
                        <View style={[styles.progressBarFill, { width: `${progress}%`, backgroundColor: isCompleted ? C.teal : item.lock_funds ? C.purple : C.green }]} />
                    </View>
                    <Text style={styles.progressPct}>{progress.toFixed(0)}%</Text>
                </View>
                {!isCompleted && (
                    <Text style={styles.goalRemaining}>KSH {remaining.toLocaleString()} remaining</Text>
                )}

                {!isCompleted && (
                    <TouchableOpacity style={styles.depositBtn} onPress={() => { setSelectedGoal(item); setDepositModal(true); }}>
                        <Ionicons name="add-circle" size={14} color={C.white} />
                        <Text style={styles.depositBtnTxt}>Deposit via M-Pesa</Text>
                    </TouchableOpacity>
                )}
            </View>
        );
    };

    // ── Challenge Card ────────────────────────────────────────────────────────
    const ChallengeCard = ({ item }) => {
        const isJoined = !!item.my_participation;
        const myProgress = isJoined ? Math.min((parseFloat(item.my_participation.saved_amount) / parseFloat(item.target_amount)) * 100, 100) : 0;
        const daysLeft = Math.max(0, Math.ceil((new Date(item.end_date) - new Date()) / (1000 * 60 * 60 * 24)));
        const iconColors = { trophy: C.gold, ribbon: C.purple, star: C.teal, flame: '#EF4444' };
        const iconColor = iconColors[item.badge_icon?.split('-')[0]] || C.gold;

        return (
            <View style={[styles.challengeCard, isJoined && styles.challengeCardJoined]}>
                <View style={[styles.challengeAccentTop, { backgroundColor: isJoined ? C.green : iconColor }]} />
                <View style={styles.challengeHead}>
                    <View style={[styles.challengeIconWrap, { backgroundColor: iconColor + '20' }]}>
                        <Ionicons name={item.badge_icon || 'trophy-outline'} size={22} color={iconColor} />
                    </View>
                    <View style={{ flex: 1, marginLeft: 10 }}>
                        <Text style={styles.challengeTitle}>{item.title}</Text>
                        <View style={{ flexDirection: 'row', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                            <CadenceBadge cadence={item.cadence} />
                            <StatusChip status={isJoined ? item.my_participation.status : item.status} />
                        </View>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.challengeXp}>+{item.xp_reward} XP</Text>
                        <Text style={styles.challengeDays}>{daysLeft}d left</Text>
                    </View>
                </View>

                {item.description ? <Text style={styles.challengeDesc}>{item.description}</Text> : null}

                <View style={styles.challengeStats}>
                    <View style={styles.challengeStat}>
                        <Text style={styles.challengeStatVal}>KSH {parseFloat(item.target_amount).toLocaleString()}</Text>
                        <Text style={styles.challengeStatLbl}>Target</Text>
                    </View>
                    <View style={styles.challengeStatDivider} />
                    <View style={styles.challengeStat}>
                        <Text style={styles.challengeStatVal}>{item.participant_count || 0}</Text>
                        <Text style={styles.challengeStatLbl}>Participants</Text>
                    </View>
                    {item.bonus_description && <>
                        <View style={styles.challengeStatDivider} />
                        <View style={[styles.challengeStat, { flex: 2 }]}>
                            <Text style={styles.challengeStatVal} numberOfLines={1}>🎁 {item.bonus_description}</Text>
                            <Text style={styles.challengeStatLbl}>Bonus Reward</Text>
                        </View>
                    </>}
                </View>

                {isJoined && (
                    <View style={{ marginTop: 10 }}>
                        <View style={styles.progressBarBg}>
                            <View style={[styles.progressBarFill, { width: `${myProgress}%`, backgroundColor: C.green }]} />
                        </View>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                            <Text style={styles.challengeProgressTxt}>My progress: KSH {parseFloat(item.my_participation.saved_amount).toLocaleString()}</Text>
                            <Text style={styles.challengeProgressTxt}>{myProgress.toFixed(0)}%</Text>
                        </View>
                    </View>
                )}

                <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
                    {isJoined ? (
                        <TouchableOpacity style={styles.challengeDepositBtn} onPress={() => { setSelectedChallenge(item); setChallengeDepositModal(true); }}>
                            <Ionicons name="add-circle-outline" size={14} color={C.bg} />
                            <Text style={styles.challengeDepositBtnTxt}>Add Savings</Text>
                        </TouchableOpacity>
                    ) : (
                        <TouchableOpacity style={styles.joinBtn} onPress={() => handleJoinChallenge(item.id)}>
                            <Ionicons name="flag-outline" size={14} color={C.bg} />
                            <Text style={styles.joinBtnTxt}>Join Challenge</Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );
    };

    // ── Header ────────────────────────────────────────────────────────────────
    const renderHeader = () => (
        <View>
            {/* Balance Hero Card — COMPACT VERSION */}
            <LinearGradient colors={['#10B981', '#059669', '#065F46']} style={styles.balanceCard} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>
                {/* Decoration moved to BACKGROUND */}
                <View style={[styles.balanceOrb, { width: 100, height: 100, right: -20, top: -20 }]} />
                
                <View style={styles.balanceCardContent}>
                    <View style={styles.balanceCardTop}>
                        <View style={styles.balanceIconWrap}>
                            <Ionicons name="shield-checkmark" size={16} color={C.green} />
                        </View>
                        <Text style={styles.balanceLabel}>Savings Balance</Text>
                        <View style={{ flex: 1 }} />
                        <TouchableOpacity 
                            onPress={() => setShowAmounts(!showAmounts)} 
                            style={[styles.eyeBtn, { padding: 12, margin: -10 }]} // Larger hit area
                            activeOpacity={0.7}
                        >
                            <Ionicons name={showAmounts ? 'eye-outline' : 'eye-off-outline'} size={18} color="rgba(255,255,255,0.7)" />
                        </TouchableOpacity>
                    </View>
                    <Text style={styles.balanceValue}>{showAmounts ? `KSH ${totalSaved.toLocaleString()}` : '• • • •'}</Text>
                    <View style={styles.balanceStatsRow}>
                        <View style={styles.balanceStat}>
                            <Ionicons name="trending-up" size={10} color="rgba(255,255,255,0.8)" />
                            <Text style={styles.balanceStatTxt}>+5.2%</Text>
                        </View>
                        <View style={styles.balanceStatDivider} />
                        <View style={styles.balanceStat}>
                            <Ionicons name="checkmark-circle" size={10} color="rgba(255,255,255,0.8)" />
                            <Text style={styles.balanceStatTxt}>{completionRate}% rate</Text>
                        </View>
                    </View>
                </View>
            </LinearGradient>

            {/* Featured Challenge Banner */}
            {featuredChallenge && activeTab === 'goals' && (
                <TouchableOpacity 
                    style={styles.featuredBanner} 
                    onPress={() => {
                        switchTab('challenges');
                        // Optional: scrollToItem logic if needed
                    }}
                >
                    <LinearGradient colors={[C.purple, '#7C3AED']} style={styles.featuredGradient} start={{x:0, y:0}} end={{x:1, y:0}}>
                        <View style={styles.featuredIconBox}>
                            <Ionicons name={featuredChallenge.badge_icon || 'trophy'} size={20} color={C.white} />
                        </View>
                        <View style={{ flex: 1, marginLeft: 10 }}>
                            <Text style={styles.featuredTag}>POPULAR CHALLENGE</Text>
                            <Text style={styles.featuredTitle} numberOfLines={1}>{featuredChallenge.title}</Text>
                            <Text style={styles.featuredSub}>Join {featuredChallenge.participant_count} savers • Earn {featuredChallenge.xp_reward} XP</Text>
                        </View>
                        <Ionicons name="chevron-forward" size={18} color="rgba(255,255,255,0.7)" />
                    </LinearGradient>
                </TouchableOpacity>
            )}




            {/* Quick Stats — COMPACT badges layout */}
            <View style={styles.quickStatsRow}>
                {[
                    { label: 'Active', value: activeGoals.length, color: C.teal },
                    { label: 'Done', value: completedGoals.length, color: C.gold },
                    { label: 'Challenges', value: joinedChallenges.length, color: C.purple },
                ].map((s, i) => (
                    <View key={i} style={[styles.quickStat, { borderColor: s.color + '20' }]}>
                        <Text style={[styles.quickStatVal, { color: s.color }]}>{s.value}</Text>
                        <Text style={styles.quickStatLbl}>{s.label}</Text>
                    </View>
                ))}
            </View>


            {/* Loan teaser */}
            <TouchableOpacity style={styles.loanTeaser} onPress={() => { setIsFixedGoal(true); setCreateGoalModal(true); }}>
                <View style={styles.loanTeaserIcon}>
                    <Ionicons name="gift" size={20} color={C.gold} />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.loanTeaserTitle}>Unlock Interest-Free Loans</Text>
                    <Text style={styles.loanTeaserSub}>Create a Fixed Goal to qualify for loans up to 80% of your savings</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={C.gold} />
            </TouchableOpacity>

            {/* Tab Switch */}
            <View style={styles.tabBar}>
                <Animated.View style={[styles.tabIndicator, { width: tabW, transform: [{ translateX: tabAnim }] }]} />
                {[{ key: 'goals', label: 'My Goals', icon: 'wallet-outline' }, { key: 'challenges', label: 'Challenges', icon: 'trophy-outline' }].map(t => (
                    <TouchableOpacity key={t.key} style={[styles.tabBtn, { width: tabW }]} onPress={() => switchTab(t.key)}>
                        <Ionicons name={t.icon} size={14} color={activeTab === t.key ? C.green : C.textMuted} />
                        <Text style={[styles.tabLabel, activeTab === t.key && styles.tabLabelActive]}>{t.label}</Text>
                    </TouchableOpacity>
                ))}
            </View>
        </View>
    );

    if (loading) {
        return (
            <View style={[styles.container, styles.centered]}>
                <ActivityIndicator size="large" color={C.green} />
                <Text style={{ color: C.textMuted, marginTop: 12 }}>Loading savings…</Text>
            </View>
        );
    }

    const listData = activeTab === 'goals' ? goals : challenges;
    const renderItem = activeTab === 'goals'
        ? ({ item }) => <GoalCard item={item} />
        : ({ item }) => <ChallengeCard item={item} />;

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={[styles.screenHeader, { paddingTop: insets.top + 8 }]}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
                    <Ionicons name="arrow-back" size={22} color={C.text} />
                </TouchableOpacity>
                <Text style={styles.screenTitle}>My Savings</Text>
                <TouchableOpacity onPress={() => { setIsFixedGoal(false); setCreateGoalModal(true); }} style={styles.addBtn}>
                    <Ionicons name="add" size={22} color={C.green} />
                </TouchableOpacity>
            </View>


            <FlatList
                data={listData}
                renderItem={renderItem}
                keyExtractor={item => item.id?.toString()}
                ListHeaderComponent={renderHeader}
                contentContainerStyle={styles.listContent}
                showsVerticalScrollIndicator={false}
                ListEmptyComponent={
                    <View style={styles.emptyState}>
                        <Ionicons name={activeTab === 'goals' ? 'wallet-outline' : 'trophy-outline'} size={48} color={C.textFaint} />
                        <Text style={styles.emptyTitle}>{activeTab === 'goals' ? 'No savings goals yet' : 'No active challenges'}</Text>
                        <Text style={styles.emptySub}>{activeTab === 'goals' ? 'Tap + to create your first goal' : 'Check back soon for new challenges!'}</Text>
                    </View>
                }
            />

            {/* ── Create Goal Modal ──────────────────────────────────────── */}
            <Modal visible={createGoalModal} transparent animationType="slide" onRequestClose={() => setCreateGoalModal(false)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalSheet}>
                        <View style={styles.modalHandle} />
                        <Text style={styles.modalTitle}>New Savings Goal</Text>

                        <Text style={styles.modalLabel}>Goal Name</Text>
                        <TextInput style={styles.modalInput} placeholder={isFixedGoal ? 'e.g. Emergency Fund' : 'e.g. New Laptop'} placeholderTextColor={C.textMuted} value={newGoalName} onChangeText={setNewGoalName} />
                        <View style={styles.chipRow}>
                            {(isFixedGoal ? ['Business', 'Education', 'Emergency', 'Asset'] : ['Laptop', 'Vacation', 'Phone', 'Furniture']).map(s => (
                                <TouchableOpacity key={s} onPress={() => setNewGoalName(s)} style={styles.chip}><Text style={styles.chipTxt}>{s}</Text></TouchableOpacity>
                            ))}
                        </View>

                        <Text style={styles.modalLabel}>Target Amount (KSH)</Text>
                        <TextInput style={styles.modalInput} placeholder="e.g. 50000" keyboardType="numeric" placeholderTextColor={C.textMuted} value={newGoalTarget} onChangeText={setNewGoalTarget} />

                        <Text style={styles.modalLabel}>Type</Text>
                        <View style={styles.typeRow}>
                            {[{ val: false, icon: 'wallet-outline', label: 'Flexible' }, { val: true, icon: 'lock-closed-outline', label: 'Fixed (Loan eligible)' }].map(opt => (
                                <TouchableOpacity key={opt.label} style={[styles.typeOpt, isFixedGoal === opt.val && styles.typeOptActive]} onPress={() => setIsFixedGoal(opt.val)}>
                                    <Ionicons name={opt.icon} size={16} color={isFixedGoal === opt.val ? C.green : C.gray500} />
                                    <Text style={[styles.typeOptTxt, isFixedGoal === opt.val && { color: C.green }]}>{opt.label}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                        {isFixedGoal && (
                            <View style={styles.infoBox}>
                                <Ionicons name="information-circle-outline" size={16} color={C.green} />
                                <Text style={styles.infoBoxTxt}>Funds locked until goal is met. Qualifies for up to 80% interest-free loan.</Text>
                            </View>
                        )}

                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.cancelBtn} onPress={() => setCreateGoalModal(false)}><Text style={styles.cancelBtnTxt}>Cancel</Text></TouchableOpacity>
                            <TouchableOpacity style={styles.confirmBtn} onPress={handleCreateGoal}><Text style={styles.confirmBtnTxt}>Create Goal (+20 XP)</Text></TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* ── Deposit Modal ─────────────────────────────────────────────── */}
            <Modal visible={depositModal} transparent animationType="slide" onRequestClose={() => setDepositModal(false)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalSheet}>
                        <View style={styles.modalHandle} />
                        <Text style={styles.modalTitle}>Deposit to "{selectedGoal?.name}"</Text>
                        <Text style={styles.modalLabel}>Amount (KSH)</Text>
                        <TextInput style={styles.modalInput} placeholder="e.g. 1000" keyboardType="numeric" placeholderTextColor={C.textMuted} value={depositAmount} onChangeText={setDepositAmount} />
                        <Text style={styles.modalLabel}>M-Pesa Phone</Text>
                        <TextInput style={styles.modalInput} placeholder="07XXXXXXXX" keyboardType="phone-pad" placeholderTextColor={C.textMuted} value={phoneNumber} onChangeText={setPhoneNumber} />
                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.cancelBtn} onPress={() => setDepositModal(false)}><Text style={styles.cancelBtnTxt}>Cancel</Text></TouchableOpacity>
                            <TouchableOpacity style={styles.confirmBtn} onPress={handleDeposit}><Text style={styles.confirmBtnTxt}>Deposit Now</Text></TouchableOpacity>
                        </View>

                    </View>
                </View>
            </Modal>

            {/* ── Action Modal ──────────────────────────────────────────────── */}
            <Modal visible={actionModal} transparent animationType="fade" onRequestClose={() => setActionModal(false)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalSheet}>
                        <View style={styles.modalHandle} />
                        {selectedGoal && (() => {
                            const prog = Math.min((parseFloat(selectedGoal.current_amount) / parseFloat(selectedGoal.target_amount)) * 100, 100);
                            const rem = Math.max(parseFloat(selectedGoal.target_amount) - parseFloat(selectedGoal.current_amount), 0);
                            return (
                                <>
                                    <Text style={styles.modalTitle}>{selectedGoal.name}</Text>
                                    <View style={styles.actionStatRow}>
                                        <View style={styles.actionStat}><Text style={[styles.actionStatVal, { color: C.green }]}>KSH {parseFloat(selectedGoal.current_amount).toLocaleString()}</Text><Text style={styles.actionStatLbl}>Saved</Text></View>
                                        <View style={styles.actionStat}><Text style={styles.actionStatVal}>KSH {parseFloat(selectedGoal.target_amount).toLocaleString()}</Text><Text style={styles.actionStatLbl}>Target</Text></View>
                                        <View style={styles.actionStat}><Text style={[styles.actionStatVal, { color: C.red }]}>KSH {rem.toLocaleString()}</Text><Text style={styles.actionStatLbl}>Remaining</Text></View>
                                    </View>
                                    <View style={styles.progressBarBg}><View style={[styles.progressBarFill, { width: `${prog}%` }]} /></View>
                                    <Text style={[styles.progressPct, { textAlign: 'center', marginBottom: 20 }]}>{prog.toFixed(1)}% complete</Text>
                                    {selectedGoal.lock_funds && parseFloat(selectedGoal.current_amount) < parseFloat(selectedGoal.target_amount) && (
                                        <View style={[styles.infoBox, { backgroundColor: 'rgba(245,158,11,0.1)', borderColor: C.gold + '40' }]}>
                                            <Ionicons name="warning-outline" size={16} color={C.gold} />
                                            <Text style={[styles.infoBoxTxt, { color: C.gold }]}>Early withdrawal from a fixed goal may affect loan eligibility.</Text>
                                        </View>
                                    )}
                                    <TouchableOpacity style={styles.withdrawBtn} onPress={() => handleWithdraw(selectedGoal)}>
                                        <Ionicons name="cash-outline" size={16} color={C.red} />
                                        <Text style={styles.withdrawBtnTxt}>Wind Down & Withdraw</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity style={styles.cancelBtn} onPress={() => setActionModal(false)}><Text style={styles.cancelBtnTxt}>Close</Text></TouchableOpacity>
                                </>
                            );
                        })()}
                    </View>
                </View>
            </Modal>

            {/* ── Challenge Deposit Modal ───────────────────────────────────── */}
            <Modal visible={challengeDepositModal} transparent animationType="slide" onRequestClose={() => setChallengeDepositModal(false)}>
                <View style={styles.modalOverlay}>
                    <View style={styles.modalSheet}>
                        <View style={styles.modalHandle} />
                        <Text style={styles.modalTitle}>Add Savings – {selectedChallenge?.title}</Text>
                        <Text style={styles.modalLabel}>Amount (KSH)</Text>
                        <TextInput style={styles.modalInput} placeholder="e.g. 500" keyboardType="numeric" placeholderTextColor={C.textMuted} value={challengeDepositAmt} onChangeText={setChallengeDepositAmt} />
                        <Text style={styles.modalLabel}>M-Pesa Phone</Text>
                        <TextInput style={styles.modalInput} placeholder="07XXXXXXXX" keyboardType="phone-pad" placeholderTextColor={C.textMuted} value={challengePhone} onChangeText={setChallengePhone} />
                        {selectedChallenge && (
                            <View style={styles.infoBox}>
                                <Ionicons name="trophy-outline" size={16} color={C.gold} />
                                <Text style={styles.infoBoxTxt}>Hit KSH {parseFloat(selectedChallenge.target_amount || 0).toLocaleString()} to earn {selectedChallenge.xp_reward} XP{selectedChallenge.bonus_description ? ` + ${selectedChallenge.bonus_description}` : ''}!</Text>
                            </View>
                        )}
                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.cancelBtn} onPress={() => setChallengeDepositModal(false)}><Text style={styles.cancelBtnTxt}>Cancel</Text></TouchableOpacity>
                            <TouchableOpacity style={styles.confirmBtn} onPress={handleChallengeDeposit}><Text style={styles.confirmBtnTxt}>Deposit</Text></TouchableOpacity>
                        </View>

                    </View>
                </View>
            </Modal>

            {/* ── Processing Payment Notification ────────────────────────── */}
            <Modal visible={isProcessing} transparent animationType="fade">
                <View style={styles.processingOverlay}>
                    <Animated.View style={[styles.processingCard, { transform: [{ scale: pulseAnim }] }]}>
                        <View style={styles.processingIconWrap}>
                            <ActivityIndicator size="small" color={C.green} />
                        </View>
                        <View style={{ flex: 1, marginLeft: 12 }}>
                            <Text style={styles.processingTitle}>Awaiting Payment...</Text>
                            <Text style={styles.processingSub}>Please confirm the M-Pesa prompt on your phone.</Text>
                        </View>
                        <TouchableOpacity onPress={() => { setIsProcessing(false); if(pollingRef) clearInterval(pollingRef); }} style={styles.processingClose}>
                            <Ionicons name="close-circle" size={20} color={C.gray400} />
                        </TouchableOpacity>
                    </Animated.View>
                </View>
            </Modal>

        </View>
    );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: C.bg },
    centered: { justifyContent: 'center', alignItems: 'center' },

    // Screen header
    screenHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: C.border, backgroundColor: C.bg },
    screenTitle: { fontSize: 16, fontWeight: '800', color: C.text, letterSpacing: -0.3 },
    backBtn: { padding: 6 },
    addBtn: { padding: 6 },

    listContent: { padding: 16, paddingBottom: 40 },

    // Balance Card
    balanceCard: { borderRadius: 14, marginBottom: 8, overflow: 'hidden', position: 'relative', ...C.cardShadow },
    balanceCardContent: { padding: 14 },
    balanceCardTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
    balanceIconWrap: { width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginRight: 8 },
    eyeBtn: { padding: 4 },
    balanceLabel: { color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: '700' },
    balanceValue: { color: C.white, fontSize: 26, fontWeight: '800', letterSpacing: -0.5, marginBottom: 8 },
    balanceStatsRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    balanceStat: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    balanceStatTxt: { color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: '600' },
    balanceStatDivider: { width: 1, height: 10, backgroundColor: 'rgba(255,255,255,0.2)' },
    balanceOrb: { position: 'absolute', borderRadius: 75, backgroundColor: 'rgba(255,255,255,0.1)' },

    // Quick Stats
    quickStatsRow: { flexDirection: 'row', gap: 6, marginBottom: 10 },
    quickStat: { flex: 1, backgroundColor: C.bg, borderRadius: 10, borderWidth: 1, borderColor: C.border, paddingVertical: 6, paddingHorizontal: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, ...C.cardShadow },
    quickStatVal: { fontSize: 14, fontWeight: '800' },
    quickStatLbl: { color: C.textMuted, fontSize: 9, fontWeight: '700', textTransform: 'uppercase' },


    // Loan teaser
    loanTeaser: { flexDirection: 'row', backgroundColor: 'rgba(245,158,11,0.05)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.15)', borderRadius: 12, padding: 12, alignItems: 'center', gap: 10, marginBottom: 14 },
    loanTeaserIcon: { width: 36, height: 36, borderRadius: 18, backgroundColor: C.goldGlow, alignItems: 'center', justifyContent: 'center' },
    loanTeaserTitle: { color: C.gold, fontSize: 13, fontWeight: '700' },
    loanTeaserSub: { color: C.textMuted, fontSize: 11, marginTop: 2, flex: 1 },

    // Tab Bar
    tabBar: { flexDirection: 'row', backgroundColor: C.surfaceRaised, borderRadius: 10, borderWidth: 1, borderColor: C.border, padding: 3, marginBottom: 14, position: 'relative', overflow: 'hidden' },
    tabIndicator: { position: 'absolute', top: 3, left: 3, height: '100%', backgroundColor: C.white, borderRadius: 8, ...C.cardShadow },
    tabBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8, gap: 6 },
    tabLabel: { fontSize: 12, fontWeight: '600', color: C.textMuted },
    tabLabelActive: { color: C.green },

    // Goal Card
    goalCard: { backgroundColor: C.bg, borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 12, overflow: 'hidden', position: 'relative', ...C.cardShadow },
    goalAccentTop: { position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: C.purple },
    goalCardHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 14 },
    goalIconWrap: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },
    goalName: { color: C.text, fontSize: 14, fontWeight: '700' },
    moreBtn: { padding: 4 },
    goalAmounts: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 },
    goalAmtLabel: { color: C.textMuted, fontSize: 10, fontWeight: '600', marginBottom: 2 },
    goalAmtValue: { color: C.green, fontSize: 16, fontWeight: '800' },
    progressBarWrap: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
    progressBarBg: { flex: 1, height: 6, backgroundColor: C.surfaceRaised, borderRadius: 3, overflow: 'hidden' },
    progressBarFill: { height: '100%', borderRadius: 3, backgroundColor: C.green },
    progressPct: { color: C.textMuted, fontSize: 11, fontWeight: '700', minWidth: 36, textAlign: 'right' },
    goalRemaining: { color: C.textMuted, fontSize: 11, marginBottom: 10 },
    depositBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: C.green, borderRadius: 8, paddingVertical: 8, gap: 6 },
    depositBtnTxt: { color: C.white, fontSize: 12, fontWeight: '700' },

    // Mini chips
    miniChip: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 20 },
    miniChipTxt: { fontSize: 9, fontWeight: '700' },

    // Challenge Card
    challengeCard: { backgroundColor: C.bg, borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 14, overflow: 'hidden', position: 'relative', ...C.cardShadow },
    challengeCardJoined: { borderColor: C.green },
    challengeAccentTop: { position: 'absolute', top: 0, left: 0, right: 0, height: 3 },
    challengeHead: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
    challengeIconWrap: { width: 42, height: 42, borderRadius: 21, alignItems: 'center', justifyContent: 'center' },
    challengeTitle: { color: C.text, fontSize: 14, fontWeight: '800' },
    challengeXp: { color: C.gold, fontSize: 13, fontWeight: '800' },
    challengeDays: { color: C.textMuted, fontSize: 10, marginTop: 2 },
    challengeDesc: { color: C.textMuted, fontSize: 12, lineHeight: 18, marginBottom: 10 },
    challengeStats: { flexDirection: 'row', backgroundColor: C.surface, borderRadius: 8, padding: 10, marginBottom: 4 },
    challengeStat: { flex: 1, alignItems: 'center' },
    challengeStatVal: { color: C.text, fontSize: 13, fontWeight: '700' },
    challengeStatLbl: { color: C.textMuted, fontSize: 9, marginTop: 2 },
    challengeStatDivider: { width: 1, backgroundColor: C.border, marginVertical: 2 },
    challengeProgressTxt: { color: C.textMuted, fontSize: 10, fontWeight: '600' },
    challengeDepositBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: C.green, borderRadius: 8, paddingVertical: 8, gap: 6 },
    challengeDepositBtnTxt: { color: C.white, fontSize: 12, fontWeight: '700' },
    joinBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: C.gold, borderRadius: 8, paddingVertical: 8, gap: 6 },
    joinBtnTxt: { color: C.white, fontSize: 12, fontWeight: '700' },

    // Badge
    badge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, borderWidth: 1 },
    badgeTxt: { fontSize: 9, fontWeight: '700', letterSpacing: 0.5 },

    // StatusChip
    statusChip: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, borderWidth: 1 },
    statusTxt: { fontSize: 9, fontWeight: '700' },

    // Empty state
    emptyState: { alignItems: 'center', paddingVertical: 50, gap: 8 },
    emptyTitle: { color: C.textMuted, fontSize: 16, fontWeight: '700' },
    emptySub: { color: C.textFaint, fontSize: 12 },

    // Modals
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
    modalSheet: { backgroundColor: C.bg, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 36, ...C.cardShadow },
    modalHandle: { width: 38, height: 4, backgroundColor: C.border, borderRadius: 2, alignSelf: 'center', marginBottom: 20 },
    modalTitle: { color: C.text, fontSize: 17, fontWeight: '800', marginBottom: 18, textAlign: 'center' },
    modalLabel: { color: C.textMuted, fontSize: 12, fontWeight: '600', marginBottom: 6 },
    modalInput: { borderWidth: 1, borderColor: C.border, borderRadius: 10, padding: 12, color: C.text, fontSize: 14, backgroundColor: C.surface, marginBottom: 14 },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 18 },
    chip: { backgroundColor: C.surfaceRaised, borderWidth: 1, borderColor: C.border, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20 },
    chipTxt: { color: C.textMuted, fontSize: 12, fontWeight: '600' },
    typeRow: { flexDirection: 'row', gap: 10, marginBottom: 14 },
    typeOpt: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 10, borderRadius: 10, borderWidth: 1, borderColor: C.border, backgroundColor: C.bg, gap: 6 },
    typeOptActive: { borderColor: C.green, backgroundColor: C.greenGlow },
    typeOptTxt: { color: C.gray500, fontSize: 12, fontWeight: '600' },
    infoBox: { flexDirection: 'row', backgroundColor: C.greenGlow, borderWidth: 1, borderColor: 'rgba(16,185,129,0.15)', borderRadius: 10, padding: 10, gap: 8, marginBottom: 16, alignItems: 'flex-start' },
    infoBoxTxt: { color: C.green, fontSize: 11, lineHeight: 16, flex: 1 },
    modalActions: { flexDirection: 'row', gap: 10, marginTop: 4 },
    cancelBtn: { flex: 1, padding: 13, borderRadius: 10, backgroundColor: C.surfaceRaised, alignItems: 'center', borderWidth: 1, borderColor: C.border },
    cancelBtnTxt: { color: C.textMuted, fontWeight: '700', fontSize: 13 },
    confirmBtn: { flex: 2, padding: 13, borderRadius: 10, backgroundColor: C.green, alignItems: 'center' },
    confirmBtnTxt: { color: C.white, fontWeight: '800', fontSize: 13 },

    // Action Modal stats
    actionStatRow: { flexDirection: 'row', justifyContent: 'space-between', backgroundColor: C.surface, borderRadius: 10, padding: 14, marginBottom: 14 },
    actionStat: { flex: 1, alignItems: 'center' },
    actionStatVal: { color: C.text, fontSize: 14, fontWeight: '800' },
    actionStatLbl: { color: C.textMuted, fontSize: 10, marginTop: 2 },

    // Withdraw button
    withdrawBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(239,68,68,0.05)', borderWidth: 1, borderColor: 'rgba(239,68,68,0.2)', borderRadius: 10, padding: 13, marginBottom: 10, gap: 8 },
    withdrawBtnTxt: { color: C.red, fontWeight: '700', fontSize: 13 },

    // Featured Banner
    featuredBanner: { marginBottom: 12, borderRadius: 14, overflow: 'hidden', ...C.cardShadow },
    featuredGradient: { flexDirection: 'row', alignItems: 'center', padding: 12 },
    featuredIconBox: { width: 40, height: 40, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
    featuredTag: { color: 'rgba(255,255,255,0.8)', fontSize: 8, fontWeight: '800', letterSpacing: 1 },
    featuredTitle: { color: C.white, fontSize: 14, fontWeight: '800', marginVertical: 1 },
    featuredSub: { color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: '600' },

    // Processing Overlay
    processingOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', justifyContent: 'center', alignItems: 'center', padding: 20 },
    processingCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.bg, borderRadius: 16, padding: 16, ...C.cardShadow, borderLeftWidth: 4, borderLeftColor: C.green },
    processingIconWrap: { width: 42, height: 42, borderRadius: 21, backgroundColor: C.greenGlow, alignItems: 'center', justifyContent: 'center' },
    processingTitle: { color: C.text, fontSize: 15, fontWeight: '800' },
    processingSub: { color: C.textMuted, fontSize: 12, marginTop: 2 },
    processingClose: { padding: 4 },

});


export default SavingsScreen;
